import { jsx } from "react/jsx-runtime";
import { memo, useReducer, useRef, useCallback, useEffect, useMemo, useState } from "react";
import { SectionTabsContext, SectionTabsRegistrationContext, ActiveSectionsTabsContext, useSectionTabs, useActiveSectionsTabs } from "./Context.js";
import { sectionTabsMapReducer, activeSectionTabsMapReducer } from "./State.js";
import { TabButton } from "../Tabs/TabButton.js";
import { useClassNamePrefix } from "../../auxiliary/useClassNamePrefix.js";
const SectionTabsProvider = memo(({ children }) => {
  const [tabs, sectionTabsDispatch] = useReducer(sectionTabsMapReducer, {});
  const [activeTabs, activeSectionTabsDispatch] = useReducer(activeSectionTabsMapReducer, {});
  const observer = useRef(new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting, intersectionRatio, time }) => {
      const id = target.id;
      if (id) {
        activeSectionTabsDispatch({
          type: isIntersecting ? "SET" : "UNSET",
          payload: {
            id,
            time,
            intersectionRatio
          }
        });
      }
    });
  }, {
    threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1]
  }));
  const registerTab = useCallback((newTab) => {
    const element = document.getElementById(newTab.id);
    element && observer.current.observe(element);
    sectionTabsDispatch({ type: "REGISTER", payload: newTab });
  }, []);
  const unregisterTab = useCallback((oldTab) => {
    const element = document.getElementById(oldTab.id);
    element && observer.current.unobserve(element);
    activeSectionTabsDispatch({
      type: "UNSET",
      payload: { id: oldTab.id, time: 0, intersectionRatio: 0 }
    });
    sectionTabsDispatch({ type: "UNREGISTER", payload: oldTab });
  }, []);
  useEffect(() => {
    const intersectionObserver = observer.current;
    return () => {
      intersectionObserver.disconnect();
    };
  }, []);
  const sectionTabsRegistration = useMemo(() => [
    registerTab,
    unregisterTab
  ], [
    registerTab,
    unregisterTab
  ]);
  const activeSectionTabs = useMemo(() => activeTabs, [activeTabs]);
  return /* @__PURE__ */ jsx(SectionTabsContext.Provider, { value: tabs, children: /* @__PURE__ */ jsx(SectionTabsRegistrationContext.Provider, { value: sectionTabsRegistration, children: /* @__PURE__ */ jsx(ActiveSectionsTabsContext.Provider, { value: activeSectionTabs, children }) }) });
});
const SectionTabs = memo(() => {
  const prefix = useClassNamePrefix();
  const tabs = useSectionTabs();
  const activeTabs = useActiveSectionsTabs();
  const entries = Object.entries(tabs);
  const [selectedTab, setSelectedTab] = useState(void 0);
  const clickTimestamp = useRef(Date.now());
  const activeTabEntries = Object.entries(activeTabs).map(([, activeTab2]) => activeTab2).sort((a, b) => b.intersectionRatio === a.intersectionRatio ? b.time - a.time : b.intersectionRatio - a.intersectionRatio);
  const activeTab = activeTabEntries.length > 0 ? activeTabEntries[0].id : void 0;
  const selectedOrActiveTab = selectedTab || activeTab;
  useEffect(() => {
    if (selectedTab && (typeof activeTabs[selectedTab] === "undefined" || activeTab && activeTabs[activeTab]?.intersectionRatio > activeTabs[selectedTab].intersectionRatio)) {
      if (Date.now() - clickTimestamp.current > 1e3) {
        setSelectedTab(void 0);
      }
    }
  }, [selectedOrActiveTab, selectedTab, activeTab, activeTabs]);
  return entries.length > 1 ? /* @__PURE__ */ jsx("div", { className: `${prefix}section-tabs`, children: /* @__PURE__ */ jsx("div", { className: `${prefix}section-tabs-content`, children: entries.map(([, { id, label, isMeta = false }]) => /* @__PURE__ */ jsx(
    TabButton,
    {
      isSelected: selectedOrActiveTab === id,
      onClick: () => {
        const element = document.getElementById(id);
        if (element) {
          element.tabIndex = 0;
          element.focus({ preventScroll: true });
          element.blur();
          element.tabIndex = -1;
          element.scrollIntoView({ behavior: "smooth" });
          setSelectedTab(id);
          clickTimestamp.current = Date.now();
        }
      },
      children: label
    },
    id
  )) }) }) : null;
});
SectionTabs.displayName = "SectionTabs";
export {
  SectionTabs,
  SectionTabsProvider
};
//# sourceMappingURL=SectionTabs.js.map
